import React from "react";
import { useTranslation } from "react-i18next";
import { TransformedProduct } from "../../../product/types/productApiTypes";
import { useMoveSavedForLaterItemBackToCart } from "../../hooks/useMoveSavedForLaterItemBackToCart";
import { useRemoveFromSavedForLater } from "../../hooks/useRemoveFromSavedForLater";
import { useMoveSavedForLaterItemToWishlist } from "../../hooks/useMoveSavedForLaterItemToWishlist";
import { useToggleCompareItem } from "../../../compare/hooks/useToggleCompareItem";
import ProductPriceDisplay from "../../../product/components/common/ProductPriceDisplay";
import Icon from "../../../../../common/components/Icon/Icon";
import CartItemActions from "../../../cart/components/common/CartItemActions";

interface PanelSavedForLaterItemProps {
  item: TransformedProduct;
}

const PanelSavedForLaterItem: React.FC<PanelSavedForLaterItemProps> = ({
  item,
}) => {
  const { t } = useTranslation();

  const { handleMoveSavedForLaterItemBackToCart, isMoving: isMovingToCart } =
    useMoveSavedForLaterItemBackToCart();

  const { handleRemoveFromSavedForLater, isRemoving } =
    useRemoveFromSavedForLater();

  const {
    handleMoveSavedForLaterItemToWishlist,
    isMoving: isMovingToWishlist,
  } = useMoveSavedForLaterItemToWishlist();

  // Use item.id (Variation ID for saved variations) for comparison
  const { handleToggle: handleCompareToggle, isInCompare } =
    useToggleCompareItem(item.id);

  const isLoading = isMovingToCart || isRemoving || isMovingToWishlist;

  const handleAddToCartClick = () => {
    handleMoveSavedForLaterItemBackToCart(item.id);
  };

  const handleMoveToWishlistClick = () => {
    // Pass parentId explicitly
    handleMoveSavedForLaterItemToWishlist(item.id, item.parentId);
  };

  const image = item.images && item.images.length > 0 ? item.images[0] : null;

  const stockLabel =
    item.stockStatus === "outofstock"
      ? t("productCard.outOfStockBadge", "Sold Out")
      : t("productInfoDetails.inStock", "In Stock");

  const stockClass =
    item.stockStatus === "outofstock" ? "is-out-of-stock" : "is-in-stock";

  return (
    <li className="panel-saved-for-later-item">
      {/* Top Actions: Add to Cart & Remove */}
      <div className="panel-saved-for-later-item__actions panel-saved-for-later-item__actions--top">
        <button
          className="panel-saved-for-later-item__action-btn"
          onClick={handleAddToCartClick}
          disabled={isLoading || item.stockStatus === "outofstock"}
        >
          <Icon
            iconName="shopping-cart-filled"
            className="icon--stroke-thick"
          />
          <span>{t("savedForLater.moveToCart", "Add Back to Cart")}</span>
        </button>
        <button
          className="panel-saved-for-later-item__action-btn panel-saved-for-later-item__action-btn--remove"
          onClick={() => handleRemoveFromSavedForLater(item.id)}
          disabled={isLoading}
          aria-label={t("savedForLater.remove", "Remove")}
        >
          <Icon iconName="x-mark" className="icon--stroke-thickest" />
        </button>
      </div>

      <div className="panel-saved-for-later-item__content">
        {/* Image */}
        <div className="panel-saved-for-later-item__image">
          <a href={item.permalink}>
            {image ? (
              <img src={image.thumbnail} alt={image.alt || item.name} />
            ) : (
              <div className="placeholder-image"></div>
            )}
          </a>
        </div>

        {/* Details */}
        <div className="panel-saved-for-later-item__details">
          <a href={item.permalink} className="panel-saved-for-later-item__name">
            {item.name}
          </a>

          <div className="panel-saved-for-later-item__meta">
            {/* Attributes */}
            {item.enrichedAttributes?.map((attr) => {
              const term = attr.terms[0];
              if (!term) return null;
              return (
                <div key={`${attr.taxonomy}-${term.id}`}>
                  <strong>{attr.name}:</strong> {term.name}
                </div>
              );
            })}

            {/* Price & Stock row */}
            <div className="panel-saved-for-later-item__price-row">
              <ProductPriceDisplay
                product={item}
                className="panel-saved-for-later-item__price"
              />
              <span
                className={`panel-saved-for-later-item__stock-status ${stockClass}`}
              >
                {stockLabel}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 
          Bottom Actions: Move to Wishlist & Compare 
          We now use the standardized CartItemActions component here.
      */}
      <CartItemActions
        onMoveToWishlist={handleMoveToWishlistClick}
        onToggleCompare={handleCompareToggle}
        isInCompare={isInCompare}
        isMoving={isMovingToWishlist}
        itemName={item.name}
        context="panel"
        placement="bottom"
      />
    </li>
  );
};

export default PanelSavedForLaterItem;
